import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';

import { Form, PrimaryButton, SecondaryButton, FieldSelect, FieldTextInput } from '../../../components';

import css from './BlockUserForm.module.css';

// Keep this list (value + translation key) in sync with VALID_REASONS in
// server/api/block-user.js - the server is the real gate, but the dropdown options and the
// server's allow-list need to describe the same set of reasons.
export const BLOCK_REASONS = [
  'harassment_abusive',
  'scam_fraud',
  'inappropriate_content',
  'spam',
  'other',
];

const BlockUserForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        handleSubmit,
        values,
        inProgress = false,
        // Named serverError, not submitError - see ReportListingForm.js's identical note:
        // "submitError" is react-final-form's own reserved form-state field name.
        serverError,
        onCancel,
      } = fieldRenderProps;

      const intl = useIntl();
      const classes = classNames(rootClassName || css.root, className);

      const reasonRequiredMessage = intl.formatMessage({ id: 'BlockUserForm.reasonRequired' });
      const otherReasonRequiredMessage = intl.formatMessage({
        id: 'BlockUserForm.otherReasonRequired',
      });

      const isOtherReason = values?.reason === 'other';

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <p className={css.intro}>
            <FormattedMessage id="BlockUserForm.intro" />
          </p>

          <FieldSelect
            id="BlockUserForm.reason"
            name="reason"
            label={intl.formatMessage({ id: 'BlockUserForm.reasonLabel' })}
            validate={validators.required(reasonRequiredMessage)}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'BlockUserForm.reasonPlaceholder' })}
            </option>
            {BLOCK_REASONS.map(reasonKey => (
              <option key={reasonKey} value={reasonKey}>
                {intl.formatMessage({ id: `BlockUserForm.reason.${reasonKey}` })}
              </option>
            ))}
          </FieldSelect>

          {isOtherReason ? (
            <FieldTextInput
              className={css.field}
              id="BlockUserForm.otherReasonText"
              name="otherReasonText"
              type="text"
              label={intl.formatMessage({ id: 'BlockUserForm.otherReasonLabel' })}
              placeholder={intl.formatMessage({ id: 'BlockUserForm.otherReasonPlaceholder' })}
              validate={validators.requiredAndNonEmptyString(otherReasonRequiredMessage)}
            />
          ) : null}

          <FieldTextInput
            className={css.field}
            id="BlockUserForm.description"
            name="description"
            type="textarea"
            label={intl.formatMessage({ id: 'BlockUserForm.descriptionLabel' })}
            placeholder={intl.formatMessage({ id: 'BlockUserForm.descriptionPlaceholder' })}
          />

          {serverError ? <p className={css.error}>{serverError}</p> : null}

          <div className={css.buttonRow}>
            <SecondaryButton type="button" className={css.cancelButton} onClick={onCancel}>
              <FormattedMessage id="BlockUserForm.cancelButtonText" />
            </SecondaryButton>
            <PrimaryButton type="submit" inProgress={inProgress}>
              <FormattedMessage id="BlockUserForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

export default BlockUserForm;
